import { validateDimensions } from "../utils/dimension-utils";

AFRAME.registerComponent("artwork", {
  schema: {
    artworkId: { type: "string" },
    image: { type: "string" },
    frame: { type: "string", default: null },
    artworkInfo: {
      type: "string",
      parse: v => (typeof v === "object" ? v : v ? JSON.parse(v) : {}),
      stringify: v => (typeof v === "string" ? v : JSON.stringify(v))
    },
    glbModel: { type: "string" },
    mediumName: { type: "string" },
    completed: { type: "boolean", default: false },
    version: { type: "number", default: 1 },
    remoteArtwork: { type: "string" },
    canBeEdited: { type: "boolean", default: false },
    qrCodeId: { type: "string", default: "" },
    pinned: { type: "boolean", default: false },
    isArtwork: { type: "boolean", default: false },
    myMediaId: { type: "number" },
    isDigitalFile: { type: "boolean" },
    hideArtworkLabel: { type: "boolean", default: false }
  },
  init() {
    // Component initialized
    this.dimensionScalingApplied = false;
    this.mediaLoadedHandler = this.onMediaLoaded.bind(this);

    // Listen for media loaded to apply dimension scaling
    this.el.addEventListener("image-loaded", this.mediaLoadedHandler);
    this.el.addEventListener("medium-loaded", this.mediaLoadedHandler);

    // If glbModel is set on init, create medium immediately
    if (this.data.glbModel) {
      this.updateMedium();
    }
  },

  remove() {
    this.el.removeEventListener("image-loaded", this.mediaLoadedHandler);
    this.el.removeEventListener("medium-loaded", this.mediaLoadedHandler);
  },

  onMediaLoaded() {
    // Apply dimension scaling once media is loaded
    if (!this.dimensionScalingApplied) {
      this.applyDimensionScaling();
    }
  },

  // Helper method to determine if this artwork is an image or 3D model
  isImageContent() {
    // Check 1: Media component presence (most reliable)
    if (this.el.components["media-image"]) {
      return true; // Definitely an image
    }
    if (this.el.components["gltf-model-plus"]) {
      return false; // Definitely a 3D model
    }

    // Check 2: Asset metadata type
    const assetMetadata = this.el.components["asset-metadata"];
    if (assetMetadata && assetMetadata.data.assetType) {
      const assetType = assetMetadata.data.assetType.toLowerCase();
      if (assetType.includes("image") || assetType.includes("photo") || assetType.includes("picture")) {
        return true;
      }
      if (
        assetType.includes("model") ||
        assetType.includes("3d") ||
        assetType.includes("gltf") ||
        assetType.includes("obj")
      ) {
        return false;
      }
    }

    // Check 3: File extension from media-loader src
    const mediaLoader = this.el.components["media-loader"];
    if (mediaLoader && mediaLoader.data.src) {
      const url = mediaLoader.data.src.toLowerCase();
      const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".tiff", ".ico"];
      const modelExtensions = [".gltf", ".glb", ".obj", ".fbx", ".dae", ".3ds", ".ply", ".stl"];

      if (imageExtensions.some(ext => url.includes(ext))) {
        return true;
      }
      if (modelExtensions.some(ext => url.includes(ext))) {
        return false;
      }
    }

    // Fallback: Check if we have a medium component (legacy logic)
    // If there's a medium, it's likely a 2D artwork regardless of other factors
    const hasMedium = this.el.querySelector("[artwork-medium]") !== null;
    if (hasMedium) {
      return true; // Mediums are for 2D artworks
    }

    // Final fallback: assume image if we can't determine
    console.warn("[artwork] Could not determine content type, defaulting to image");
    return true;
  },

  applyDimensionScaling() {
    if (this.dimensionScalingApplied) {
      return;
    }

    const artworkInfo = this.data.artworkInfo;

    // Only apply if we have valid dimensions
    if (!artworkInfo || !artworkInfo.dimensionsX || !artworkInfo.dimensionsY) {
      return;
    }

    // Validate dimensions
    const validation = validateDimensions(artworkInfo.dimensionsX, artworkInfo.dimensionsY, artworkInfo.dimensionsZ);

    if (!validation.valid) {
      console.warn("[artwork] Invalid dimensions, not applying scaling:", validation.issues);
      return;
    }

    // Get current artwork dimensions (from mesh bounding box)
    const currentDimensions = this.getCurrentArtworkDimensions();

    if (!currentDimensions || currentDimensions.width === 0 || currentDimensions.height === 0) {
      console.warn("[artwork] Could not determine current artwork dimensions");
      return;
    }

    // Normalize dimensions by the current parent scale to get original mesh size
    const currentScale = this.el.object3D?.scale || { x: 1, y: 1, z: 1 };

    const originalDimensions = {
      width: currentDimensions.width / (currentScale.x || 1),
      height: currentDimensions.height / (currentScale.y || 1),
      depth: currentDimensions.depth / (currentScale.z || 1)
    };

    if (originalDimensions.width === 0 || originalDimensions.height === 0) {
      console.warn("[artwork] Normalized dimensions are zero, skipping scaling");
      return;
    }

    // Calculate scale needed to reach target dimensions
    const targetWidth = validation.dimensions.width;
    const targetHeight = validation.dimensions.height;
    const targetDepth = validation.dimensions.depth;

    // Determine if this is a 3D model or flat image using improved detection
    const isImageContent = this.isImageContent();
    const hasSignificantDepth = originalDimensions.depth > 0.01; // More than 1cm depth

    // For images, always treat as 2D regardless of reported depth
    // For 3D models, check if depth is significant
    const is3DModel = !isImageContent && hasSignificantDepth;

    let scaleX, scaleY, scaleZ;

    if (is3DModel && targetDepth) {
      // For 3D models with depth metadata: scale uniformly to preserve proportions
      // Use the smallest scale to ensure model fits within (contains) specified dimensions
      const scaleByWidth = targetWidth / originalDimensions.width;
      const scaleByHeight = targetHeight / originalDimensions.height;
      const scaleByDepth = targetDepth / originalDimensions.depth;

      // Use Math.min to contain the artwork within dimensions (like CSS object-fit: contain)
      const uniformScale = Math.min(scaleByWidth, scaleByHeight, scaleByDepth);

      scaleX = scaleY = scaleZ = uniformScale;
    } else if (is3DModel) {
      // For 3D models without depth metadata: scale uniformly based on width/height
      const scaleByWidth = targetWidth / originalDimensions.width;
      const scaleByHeight = targetHeight / originalDimensions.height;

      // Use the smaller scale to preserve proportions and fit within bounds
      const uniformScale = Math.min(scaleByWidth, scaleByHeight);

      scaleX = scaleY = scaleZ = uniformScale;
    } else {
      // For flat images/canvases: use uniform scaling to preserve aspect ratio
      // CRITICAL: In art visualization, distortion is unacceptable. We must preserve
      // the image's natural aspect ratio even if it doesn't match metadata exactly.
      // This is because museum metadata may refer to paper size, while the image
      // file may be cropped to the actual artwork area.

      const scaleByWidth = targetWidth / originalDimensions.width;
      const scaleByHeight = targetHeight / originalDimensions.height;

      // Use uniform scaling (like CSS object-fit: contain) to preserve aspect ratio
      // The image will fit within target dimensions without distortion
      const uniformScale = Math.min(scaleByWidth, scaleByHeight);

      scaleX = scaleY = uniformScale;
      scaleZ = 1;
    }

    // Apply scale to artwork parent entity
    // This will scale medium, frame, and info (info compensates with inverse scale)
    this.el.setAttribute("scale", `${scaleX} ${scaleY} ${scaleZ}`);
    this.dimensionScalingApplied = true;

    // Emit event for any components that need to react
    this.el.emit("artwork-scaled", { scaleX, scaleY, scaleZ });
  },

  getCurrentArtworkDimensions() {
    // Try to get dimensions from medium first, then fallback to media-image
    let mesh = null;

    // Check if there's a medium child
    const children = this.el.children || [];
    for (let i = 0; i < children.length; i++) {
      if (children[i].components && children[i].components["artwork-medium"]) {
        mesh = children[i].object3DMap.mesh;
        break;
      }
    }

    // Fallback to direct media-image mesh
    if (!mesh && this.el.object3DMap.mesh) {
      mesh = this.el.object3DMap.mesh;
    }

    if (!mesh) {
      return null;
    }

    // Calculate bounding box
    const bbox = new THREE.Box3().setFromObject(mesh);
    const size = bbox.getSize(new THREE.Vector3());

    return {
      width: size.x,
      height: size.y,
      depth: size.z
    };
  },

  update(oldData) {
    if (this.data.artworkInfo !== oldData.artworkInfo) {
      this.updateArtworkInfo();

      // Check if dimensions changed and re-apply scaling
      const oldDims = oldData.artworkInfo;
      const newDims = this.data.artworkInfo;
      const normalize = value => {
        if (value === null || value === undefined || value === "") {
          return null;
        }
        const num = Number(value);
        return Number.isNaN(num) ? value : num;
      };

      const oldX = normalize(oldDims?.dimensionsX);
      const oldY = normalize(oldDims?.dimensionsY);
      const oldZ = normalize(oldDims?.dimensionsZ);

      const newX = normalize(newDims?.dimensionsX);
      const newY = normalize(newDims?.dimensionsY);
      const newZ = normalize(newDims?.dimensionsZ);

      if (oldX !== newX || oldY !== newY || oldZ !== newZ) {
        // Reset flag to allow re-application
        this.dimensionScalingApplied = false;
        // Re-apply dimension scaling with new values
        setTimeout(() => {
          this.applyDimensionScaling();
        }, 100); // Small delay to ensure children are updated
      }
    }

    // Update frame when frame data changes (detect any change, including removal)
    if (this.data.frame !== oldData.frame) {
      this.updateFrame();
    }

    // Update medium when glbModel changes (detect any change, including removal)
    if (this.data.glbModel !== oldData.glbModel) {
      this.updateMedium();
    }
  },
  createBoundingBoxFromObject(obj) {
    return new THREE.Box3().setFromObject(obj);
  },
  udpateQrCode() {
    // Find QR code among children
    let qrEl = null;
    const children = this.el.children || [];
    for (let i = 0; i < children.length; i++) {
      if (children[i].components && children[i].components["artwork-qr"]) {
        qrEl = children[i];
        break;
      }
    }

    const qrData = {
      artworkInfo: this.data.artworkInfo,
      image: this.data.image,
      remoteArtwork: this.data.remoteArtwork,
      mediumName: this.data.mediumName,
      glbModel: this.data.glbModel,
      frame: this.data.frame,
      completed: this.data.completed,
      qrCodeId: this.data.qrCodeId,
      myMediaId: this.data.myMediaId,
      isDigitalFile: this.data.isDigitalFile,
      hideArtworkLabel: this.data.hideArtworkLabel
    };

    if (qrEl) {
      qrEl.setAttribute("artwork-qr", qrData);
      return;
    }

    const qrEntity = document.createElement("a-entity");
    this.el.appendChild(qrEntity);

    qrEntity.setAttribute("artwork-qr", qrData);
  },
  updateArtworkInfo() {
    // Find and remove existing artwork-info among children
    const children = this.el.children || [];
    for (let i = 0; i < children.length; i++) {
      if (children[i].components && children[i].components["artwork-info"]) {
        children[i].remove();
        break;
      }
    }

    const infoData = {
      artworkInfo: this.data.artworkInfo,
      mediumName: this.data.mediumName,
      isDefaultMedium: !!this.data.image,
      image: this.data.image,
      hideArtworkLabel: this.data.hideArtworkLabel
    };

    const childEntity = document.createElement("a-entity");
    this.el.appendChild(childEntity);

    childEntity.setAttribute("artwork-info", infoData);
  },
  extractDimensionsCm(str) {
    // Pattern for 'sheet' dimensions
    const sheetPattern = /sheet:\s*([\d.]+)\s*×\s*([\d.]+)\s*cm/;
    // Direct centimeter measurements pattern
    const directCmPattern = /([\d.]+)\s*x\s*([\d.]+)\s*cm/;
    // Pattern to extract centimeter measurements from a mixed string
    const mixedCmPattern = /\(([\d.]+)\s*x\s*([\d.]+)(?:\s*x\s*([\d.]+))?\s*cm\)/;
    let match = str.match(mixedCmPattern);
    if (!match) {
      match = str.match(sheetPattern);
    }
    if (!match) {
      match = str.match(directCmPattern);
    }

    if (match) {
      // Interpreting the first extracted value as height and the second as width
      const heightCm = parseFloat(match[1]);
      const widthCm = parseFloat(match[2]);
      const depthCm = match[3] ? parseFloat(match[3]) : null;
      return { heightCm, widthCm, depthCm };
    } else {
      return null;
    }
  },
  async loadArtwork() {
    // const glbModel = this.data.glbModel;
    // this.el.removeAttribute("gltf-model-plus");
    // this.el.setAttribute("gltf-model-plus", { src: glbModel });
    // this.updateMaterial();
  },
  async onModelLoaded() {
    setTimeout(() => {
      this.updateArtworkInfo();
    }, 300);
  },
  async handlePresentedArtwork() {
    this.el.emit("reset-camera");
  },
  updateFrame() {
    // Find frame among children
    let frameEl = null;
    const children = this.el.children || [];
    for (let i = 0; i < children.length; i++) {
      if (children[i].components && children[i].components["artwork-frame"]) {
        frameEl = children[i];
        break;
      }
    }

    // If no frame data, remove existing frame component if present
    if (!this.data.frame) {
      if (frameEl) {
        frameEl.remove();
      }
      return;
    }

    // Update or create frame
    if (frameEl) {
      frameEl.setAttribute("artwork-frame", {
        model: this.data.frame,
        completed: this.data.completed
      });
      return;
    }

    const frameEntity = document.createElement("a-entity");
    this.el.appendChild(frameEntity);

    frameEntity.setAttribute("artwork-frame", {
      model: this.data.frame,
      completed: this.data.completed
    });
  },
  updateMedium() {
    // Find medium among children
    let mediumEl = null;
    const children = this.el.children || [];
    for (let i = 0; i < children.length; i++) {
      if (children[i].components && children[i].components["artwork-medium"]) {
        mediumEl = children[i];
        break;
      }
    }

    // If no glbModel data, remove existing medium and show original image mesh
    if (!this.data.glbModel) {
      if (mediumEl) {
        mediumEl.remove();
      }
      // Show the original media-image mesh
      if (this.el.object3DMap && this.el.object3DMap.mesh) {
        this.el.object3DMap.mesh.visible = true;
      }
      return;
    }

    // Hide the media-image mesh since we're using a medium instead
    if (this.el.object3DMap && this.el.object3DMap.mesh) {
      this.el.object3DMap.mesh.visible = false;
    }

    // Get image URL from media component
    let imageUrl = this.data.image;
    if (!imageUrl && this.el.components["media-image"]) {
      imageUrl = this.el.components["media-image"].data.src;
    }

    // Let artwork-medium component handle scaling internally
    const mediumData = {
      glbModel: this.data.glbModel,
      mediumName: this.data.mediumName,
      image: imageUrl
    };

    if (mediumEl) {
      mediumEl.setAttribute("artwork-medium", mediumData);
      return;
    }

    // Create new medium entity
    const mediumEntity = document.createElement("a-entity");
    this.el.appendChild(mediumEntity);

    mediumEntity.setAttribute("artwork-medium", mediumData);
  }
});
